import { createContext, useCallback, useContext, useState, type ReactNode } from "react"
import { AlertTriangle, Info, X } from "lucide-react"

import { useT } from "@/lib/i18n/AdminI18nProvider"

// ── Types ────────────────────────────────────────────────────────

type ConfirmVariant = "default" | "danger"

type ConfirmOptions = {
  title: string
  message: string
  variant?: ConfirmVariant
  confirmLabel?: string
  cancelLabel?: string
}

type PromptOptions = {
  title: string
  message?: string
  label?: string
  placeholder?: string
  defaultValue?: string
  required?: boolean
}

type ConfirmState = {
  type: "confirm"
  options: ConfirmOptions
  resolve: (value: boolean) => void
}

type PromptState = {
  type: "prompt"
  options: PromptOptions
  resolve: (value: string | null) => void
}

type DialogState = ConfirmState | PromptState | null

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>
  prompt: (options: PromptOptions | string) => Promise<string | null>
}

// ── Context ──────────────────────────────────────────────────────

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>")
  return ctx
}

// ── Provider ─────────────────────────────────────────────────────

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const t = useT()
  const [dialog, setDialog] = useState<DialogState>(null)
  const [value, setValue] = useState("")
  const [loading, setLoading] = useState(false)

  const normalizeConfirm = useCallback(
    (raw: ConfirmOptions | string): ConfirmOptions => {
      if (typeof raw === "string") return { title: t("common.confirmTitle"), message: raw }
      return raw
    },
    [t],
  )

  const normalizePrompt = useCallback(
    (raw: PromptOptions | string): PromptOptions => {
      if (typeof raw === "string") return { title: t("common.info"), label: raw }
      return raw
    },
    [t],
  )

  const confirm = useCallback(
    (options: ConfirmOptions | string): Promise<boolean> => {
      return new Promise((resolve) => {
        setDialog({ type: "confirm", options: normalizeConfirm(options), resolve })
      })
    },
    [normalizeConfirm],
  )

  const prompt = useCallback(
    (options: PromptOptions | string): Promise<string | null> => {
      return new Promise((resolve) => {
        setValue(normalizePrompt(options).defaultValue ?? "")
        setDialog({ type: "prompt", options: normalizePrompt(options), resolve })
      })
    },
    [normalizePrompt],
  )

  const close = useCallback(() => {
    setDialog(null)
    setValue("")
    setLoading(false)
  }, [])

  const isDanger = dialog?.type === "confirm" && dialog.options.variant === "danger"

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}

      {dialog && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/45 p-4 animate-in fade-in duration-200"
          onClick={close}
        >
          <div
            className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isDanger ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                {isDanger ? <AlertTriangle className="h-5 w-5" /> : <Info className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-foreground">{dialog.options.title}</h2>
                {dialog.options.message && (
                  <p className="mt-1.5 whitespace-pre-wrap text-sm text-muted-foreground">{dialog.options.message}</p>
                )}
              </div>
              <button onClick={close} className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors" aria-label={t("common.close")}>
                <X className="h-4 w-4" />
              </button>
            </div>

            {dialog.type === "prompt" && (
              <div className="mt-4">
                {dialog.options.label && (
                  <label className="mb-1.5 block text-xs font-medium text-foreground">{dialog.options.label}</label>
                )}
                <input
                  autoFocus
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={dialog.options.placeholder ?? ""}
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      if (!dialog.options.required || value.trim()) {
                        close()
                        dialog.resolve(value || null)
                      }
                    }
                    if (e.key === "Escape") {
                      close()
                      dialog.resolve(null)
                    }
                  }}
                />
                {dialog.options.required && (
                  <p className="mt-1 text-xs text-muted-foreground">{t("common.confirmHint")}</p>
                )}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              {dialog.type === "confirm" ? (
                <>
                  <button
                    onClick={() => { close(); dialog.resolve(false) }}
                    className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                  >
                    {dialog.options.cancelLabel ?? t("common.dismiss")}
                  </button>
                  <button
                    onClick={async () => {
                      setLoading(true)
                      try { dialog.resolve(true) } finally { close() }
                    }}
                    className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${isDanger ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                    disabled={loading}
                  >
                    {dialog.options.confirmLabel ?? (isDanger ? t("common.delete") : t("common.confirm"))}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { close(); dialog.resolve(null) }}
                    className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={() => {
                      if (dialog.options.required && !value.trim()) return
                      close()
                      dialog.resolve(value || null)
                    }}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {t("common.confirm")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}
